import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AnalogyLogo from "@/components/AnalogyLogo";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-sm bg-white card-flat p-8">
        <AnalogyLogo size={26} className="text-slate-900 mb-6" />

        {!sent ? (
          <>
            <div className="label-overline mb-3">Reset password</div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Forgot your password?</h1>
            <p className="text-slate-500 text-sm mt-2">Enter your work email and we&apos;ll send you a reset link. It expires in 30 minutes.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="fp-email" className="label-overline">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@analogydesign.co"
                  className="mt-2 rounded-sm"
                  data-testid="forgot-password-email"
                  required
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="forgot-password-submit">
                {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                {sending ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="py-4 text-center" data-testid="forgot-password-sent">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-green-600" />
            <h1 className="font-display text-xl font-bold text-slate-900">Check your email</h1>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              If an account exists for <b>{email}</b>, a reset link is on its way. It&apos;s good for 30 minutes.
            </p>
          </div>
        )}

        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#F54800] hover:underline" data-testid="forgot-password-back-link">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
